import { FormEvent, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Cloud,
  Eye,
  EyeOff,
  LockKeyhole,
  RadioTower,
  ShieldCheck,
  Shield,
  UserRound
} from "lucide-react";
import clsx from "clsx";
import { isCloudConfigured } from "../lib/supabase";
import type { NavKey } from "../types";

type LoginScreenProps = {
  onLogin: (login: string, password: string, remember: boolean, targetView?: NavKey, adminOnly?: boolean) => string | null;
  onRegister: (login: string, password: string, name: string) => string | null;
  onChangePassword: (login: string, oldPassword: string, newPassword: string) => string | null;
  onPasswordResetRequest: (login: string) => string | null;
};

type AuthMode = "login" | "register" | "changePassword" | "forgotPassword";

export function LoginScreen({ onLogin, onRegister, onChangePassword, onPasswordResetRequest }: LoginScreenProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [adminMode, setAdminMode] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("login");

  function login() {
    const message = onLogin(username, password, remember, adminMode ? "administracja" : undefined, adminMode);
    setError(message ?? "");
    setMessage("");
  }

  function switchMode(mode: AuthMode) {
    setAuthMode(mode);
    setError("");
    setMessage("");
  }

  function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (authMode === "register") {
      const registrationError = onRegister(username, password, name);
      setError(registrationError ?? "");
      setMessage(registrationError ? "" : "Rejestracja wysłana do administratora. Konto aktywuje panel SNG Admin.");
      if (!registrationError) {
        setName("");
        setPassword("");
        setAuthMode("login");
      }
      return;
    }

    if (authMode === "changePassword") {
      const changeError = onChangePassword(username, password, newPassword);
      setError(changeError ?? "");
      setMessage(changeError ? "" : "Hasło zostało zmienione. Możesz się zalogować.");
      if (!changeError) {
        setPassword("");
        setNewPassword("");
        setAuthMode("login");
      }
      return;
    }

    if (authMode === "forgotPassword") {
      const resetError = onPasswordResetRequest(username);
      setError(resetError ?? "");
      setMessage(resetError ? "" : "Prośba o reset hasła trafiła do panelu administratora.");
      if (!resetError) {
        setAuthMode("login");
      }
      return;
    }

    login();
  }

  const heading =
    authMode === "register"
      ? "Rejestracja"
      : authMode === "changePassword"
        ? "Zmiana hasła"
        : authMode === "forgotPassword"
          ? "Reset hasła"
          : adminMode
            ? "SNG Admin"
            : "Witaj ponownie";

  return (
    <main className="login-shell">
      <section className="login-visual" aria-label="Panel marki">
        <div className="brand-row">
          <div className="brand-mark">
            <span>SN</span>
            <strong>G</strong>
          </div>
          <button
            className={clsx("admin-entry-button", adminMode && "is-active")}
            type="button"
            onClick={() => {
              setAdminMode((value) => !value);
              setAuthMode("login");
              setError("");
              setMessage("");
            }}
          >
            <Shield size={16} />
            <span>SNG Admin</span>
          </button>
          <div className="cloud-pill">
            <Cloud size={16} />
            <span>{isCloudConfigured ? "Chmura online" : "Tryb lokalny"}</span>
          </div>
        </div>

        <img className="login-truck-photo" src="/assets/sng-hero.png" alt="" aria-hidden="true" />

        <div className="visual-copy">
          <p><ShieldCheck size={16} /> Uprawnienia dopasowane do roli</p>
          <p><RadioTower size={16} /> Grafik i dyspozycja w jednym widoku</p>
          <p><BadgeCheck size={16} /> Gotowe pod darmową chmurę Supabase</p>
        </div>
      </section>

      <section className="login-panel" aria-label="Logowanie">
        <div className="window-drag" />
        <div className="login-heading">
          <div>
            <span className="eyebrow">SNG Dispatch</span>
            <h1>{heading}</h1>
          </div>
          <div className="role-badge">
            {adminMode ? <Shield size={16} /> : <UserRound size={16} />}
            <span>{adminMode ? "PANEL ADMINA" : authMode === "login" ? "LOGOWANIE" : "KONTO"}</span>
          </div>
        </div>

        <form className="login-form" onSubmit={submitLogin}>
          {authMode === "register" && (
            <label className="field">
              <span>Imię i nazwisko / nazwa</span>
              <div className="field-control">
                <UserRound size={18} />
                <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
              </div>
            </label>
          )}

          <label className="field">
            <span>Nazwa użytkownika</span>
            <div className="field-control">
              <UserRound size={18} />
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
            </div>
          </label>

          {authMode !== "forgotPassword" && (
            <label className="field">
              <span>{authMode === "changePassword" ? "Stare hasło" : "Hasło"}</span>
              <div className="field-control">
                <LockKeyhole size={18} />
                <input
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete={authMode === "register" ? "new-password" : "current-password"}
                />
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>
          )}

          {authMode === "changePassword" && (
            <label className="field">
              <span>Nowe hasło</span>
              <div className="field-control">
                <LockKeyhole size={18} />
                <input
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                />
              </div>
            </label>
          )}

          {authMode === "login" && (
            <div className="form-row">
              <label className="check">
                <input checked={remember} onChange={(event) => setRemember(event.target.checked)} type="checkbox" />
                <span>Zapamiętaj mnie</span>
              </label>
              <button className="text-button" type="button" onClick={() => switchMode("forgotPassword")}>Nie pamiętasz hasła?</button>
            </div>
          )}

          {error && <div className="login-error">{error}</div>}
          {message && <div className="login-success">{message}</div>}

          <button className="primary-button" type="submit">
            <ArrowRight size={18} />
            {authMode === "register"
              ? "Wyślij rejestrację"
              : authMode === "changePassword"
                ? "Zmień hasło"
                : authMode === "forgotPassword"
                  ? "Wyślij prośbę"
                  : adminMode
                    ? "Otwórz panel admina"
                    : "Zaloguj się"}
          </button>
        </form>

        <div className="login-actions">
          <button type="button" onClick={() => switchMode(authMode === "register" ? "login" : "register")}>
            {authMode === "register" ? "Powrót do logowania" : "Rejestracja"}
          </button>
          <button type="button" onClick={() => switchMode(authMode === "changePassword" ? "login" : "changePassword")}>
            {authMode === "changePassword" ? "Powrót" : "Zmiana hasła"}
          </button>
        </div>

        <footer className="status-line">
          <span className={clsx("status-dot", isCloudConfigured && "is-online")} />
          <span>{isCloudConfigured ? "Połączenie: OK" : "Połączenie: lokalne"}</span>
          <span>Wersja 0.1.0</span>
        </footer>
      </section>
    </main>
  );
}
